import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Stack, Paper, TextField,
  IconButton, Tooltip, Chip, Alert, useTheme, Tab, Tabs,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MapView from './MapView';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { convertCoordinate, parseAnyCoordinate } from '../../utils/geoUtils';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';
import L from 'leaflet';

function CoordinateConverter() {
  const theme = useTheme();
  const [input, setInput] = useState('40.7128, -74.0060');
  const [batchInput, setBatchInput] = useState('');
  const [mode, setMode] = useState(0); // 0 = single, 1 = batch
  const [error, setError] = useState('');
  const [copyFeedback, setCopyFeedback] = useState('');

  const singleResult = useMemo(() => {
    if (mode !== 0 || !input.trim()) return null;
    const parsed = parseAnyCoordinate(input);
    if (!parsed) {
      setError('Could not parse coordinate. Try formats: DD, DMS, or Geohash');
      return null;
    }
    setError('');
    return { lat: parsed[0], lon: parsed[1], conversions: convertCoordinate(parsed[0], parsed[1]) };
  }, [input, mode]);

  const batchResults = useMemo(() => {
    if (mode !== 1 || !batchInput.trim()) return [];
    const lines = batchInput.split('\n').filter(l => l.trim());
    return lines.map((line, i) => {
      const parsed = parseAnyCoordinate(line);
      if (!parsed) return { line: i + 1, input: line, error: 'Invalid', conversions: null };
      return { line: i + 1, input: line, lat: parsed[0], lon: parsed[1], conversions: convertCoordinate(parsed[0], parsed[1]) };
    });
  }, [batchInput, mode]);

  const mapGeoJson = useMemo(() => {
    const features = [];
    if (mode === 0 && singleResult) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [singleResult.lon, singleResult.lat] },
        properties: { name: 'Converted Point' },
      });
    } else if (mode === 1) {
      batchResults.filter(r => r.conversions).forEach((r, i) => {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [r.lon, r.lat] },
          properties: { name: `Point ${r.line}`, index: i + 1 },
        });
      });
    }
    if (features.length === 0) return null;
    return { type: 'FeatureCollection', features };
  }, [singleResult, batchResults, mode]);

  const handleCopy = useCallback((text, label) => {
    navigator.clipboard.writeText(text);
    setCopyFeedback(label);
    setTimeout(() => setCopyFeedback(''), 1500);
  }, []);

  const handleSwap = useCallback(() => {
    if (!singleResult) return;
    setInput(`${singleResult.lon}, ${singleResult.lat}`);
  }, [singleResult]);

  const pointToLayerFn = useCallback((feature, latlng) => {
    return L.circleMarker(latlng, {
      radius: 7, fillColor: '#6366f1', color: '#fff', weight: 2, fillOpacity: 0.9,
    }).bindTooltip(feature.properties.name, { permanent: false, direction: 'top' });
  }, []);

  const formatRows = singleResult ? [
    { label: 'Decimal Degrees (lat, lon)', value: singleResult.conversions.dd, key: 'dd' },
    { label: 'Decimal Degrees (lon, lat)', value: singleResult.conversions.ddReversed, key: 'ddRev' },
    { label: 'DMS', value: singleResult.conversions.dms, key: 'dms' },
    { label: 'DDM', value: singleResult.conversions.ddm, key: 'ddm' },
    { label: 'UTM', value: singleResult.conversions.utm, key: 'utm' },
    { label: 'Geohash', value: singleResult.conversions.geohash, key: 'geohash' },
    { label: 'GeoJSON [lon, lat]', value: singleResult.conversions.geoJsonArray, key: 'geojson' },
  ] : [];

  return (
    <>
      <SEO
        title="Coordinate Converter & Formatter | Free Online Tool | UmbrellaCo"
        description="Convert GPS coordinates between Decimal Degrees (DD), Degrees Minutes Seconds (DMS), UTM, and Geohash. Supports single and batch conversions with map visualization."
        keywords="coordinate converter, lat lon converter, dms to decimal, utm to lat lon, geohash converter, batch coordinate converter"
        url="/spatial-tools/coordinate-converter"
        jsonLd={createSoftwareAppSchema(
          'Coordinate Converter & Formatter',
          'Convert GPS coordinates between Decimal Degrees, DMS, UTM, and Geohash.',
          'https://umbrellaco.in/spatial-tools/coordinate-converter',
          ['Coordinate Conversion', 'Geohash Generator', 'UTM Calculator']
        )}
      />
      <ToolPageContainer
        title="Coordinate Converter & Formatter"
        description="Translate GPS coordinates across multiple formats (DD, DMS, UTM, Geohash). Supports single point conversions and bulk batch processing."
        disablePadding
      >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PanelGroup direction="horizontal">
        <Panel defaultSize={38} minSize={25}>
          <Box sx={{
            height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto',
            bgcolor: 'background.paper', borderRight: `1px solid ${theme.palette.divider}`,
          }}>
            {/* Mode Tabs */}
            <Box sx={{ px: 2, pt: 2 }}>
              <Tabs value={mode} onChange={(e, v) => setMode(v)} variant="fullWidth"
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Tab icon={<MyLocationIcon />} iconPosition="start" label="Single" />
                <Tab icon={<FormatListBulletedIcon />} iconPosition="start" label="Batch" />
              </Tabs>
            </Box>

            {/* Single Mode */}
            {mode === 0 && (
              <Box sx={{ px: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Input Coordinate</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    fullWidth size="small" value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="40.7128, -74.0060"
                    sx={{ '& input': { fontFamily: 'monospace' } }}
                  />
                  <Tooltip title="Swap lat/lon">
                    <IconButton size="small" onClick={handleSwap} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                      <SwapVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Accepts: DD, DMS, DDM, Geohash
                </Typography>

                {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}

                {copyFeedback && (
                  <Chip label={`Copied: ${copyFeedback}`} size="small" color="success" sx={{ mt: 1 }} />
                )}

                {singleResult && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Converted Formats</Typography>
                    <Stack spacing={0.8}>
                      {formatRows.map(({ label, value, key }) => (
                        <Paper key={key} variant="outlined" sx={{
                          p: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          transition: 'all 0.15s',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.04)',
                          },
                        }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                              {label}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                              {value}
                            </Typography>
                          </Box>
                          <Tooltip title="Copy">
                            <IconButton size="small" onClick={() => handleCopy(value, label)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}

            {/* Batch Mode */}
            {mode === 1 && (
              <Box sx={{ px: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Batch Input</Typography>
                <TextField
                  multiline rows={5} fullWidth size="small"
                  placeholder="One coordinate per line:&#10;40.7128, -74.0060&#10;51.5074, -0.1278&#10;48.8566, 2.3522"
                  value={batchInput} onChange={(e) => setBatchInput(e.target.value)}
                  sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />

                {batchResults.length > 0 && (
                  <Box sx={{ mt: 2, flexGrow: 1, overflow: 'auto' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Results ({batchResults.filter(r => r.conversions).length}/{batchResults.length})
                      </Typography>
                      <Button size="small" onClick={() => {
                        const csv = ['lat,lon,dms,utm,geohash',
                          ...batchResults.filter(r => r.conversions).map(r =>
                            `${r.lat},${r.lon},"${r.conversions.dms}","${r.conversions.utm}",${r.conversions.geohash}`
                          )].join('\n');
                        handleCopy(csv, 'All as CSV');
                      }}>
                        Copy All CSV
                      </Button>
                    </Stack>
                    <Stack spacing={0.5}>
                      {batchResults.map((r, i) => (
                        <Paper key={i} variant="outlined" sx={{
                          p: 1,
                          borderColor: r.error ? 'error.main' : undefined,
                          opacity: r.error ? 0.6 : 1,
                        }}>
                          {r.error ? (
                            <Typography variant="caption" color="error">Line {r.line}: {r.error} — {r.input}</Typography>
                          ) : (
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Line {r.line}
                              </Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {r.conversions.dd} → {r.conversions.dms}
                              </Typography>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                UTM: {r.conversions.utm} | GH: {r.conversions.geohash}
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Panel>

        <PanelResizeHandle style={{ width: '5px', background: theme.palette.divider, cursor: 'col-resize' }} />

        <Panel defaultSize={62} minSize={35}>
          <Box sx={{ height: '100%', width: '100%' }}>
            {mapGeoJson ? (
              <MapView
                mapId="coord-converter-map"
                geoJsonData={mapGeoJson}
                pointToLayerFn={pointToLayerFn}
                zoom={mapGeoJson.features.length === 1 ? 12 : 4}
              />
            ) : (
              <Box sx={{
                height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
              }}>
                <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center', p: 4 }}>
                  <MyLocationIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                  <Typography variant="h6" color="text.secondary">Enter a coordinate to convert</Typography>
                  <Typography variant="body2" color="text.disabled">
                    Supports DD, DMS, UTM, Geohash
                  </Typography>
                </Stack>
              </Box>
            )}
          </Box>
        </Panel>
      </PanelGroup>
      </Box>
      </ToolPageContainer>
    </>
  );
}

export default CoordinateConverter;
